import { type Currency, formatAmount } from "@loot-os/api-contract";
import type {
  StaffBlockWire,
  StaffHostDetailWire,
  StaffLiveBreakBoxStatsWire,
  StaffLiveBreakListItemWire,
} from "@loot-os/functions/src/dashboard-api/trpc/routers/liveBreakWire.js";
import { Badge } from "@loot-os/ui/components/badge";
import { BoxImage } from "@loot-os/ui/components/box-image";
import { buttonVariants } from "@loot-os/ui/components/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { DataTable } from "@loot-os/ui/components/data-table";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import { createDataTableColumns } from "@loot-os/ui/lib/dataTable";
import { formatNumber } from "@loot-os/ui/lib/format";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import { ErrorAlert } from "../components/ErrorAlert";
import { Fact, FactGroup, FactList, Stat } from "../components/Facts";
import { BlockShopperDialog, UnblockButton } from "../components/LiveBreakBlockControls";
import {
  AllowLiveBreakHostButton,
  RemoveLiveBreakHostButton,
} from "../components/LiveBreakHostListControls";
import { LiveBreakHostRateDialog } from "../components/LiveBreakHostRateDialog";
import { ShortDate } from "../components/ShortDate";
import { amountColumnHeader, amountInputValue, useCurrency } from "../lib/currency";
import { formatDate } from "../lib/format";
import {
  LIVE_BREAK_STATE_BADGE,
  LIVE_BREAK_STATE_LABELS,
  liveBreakDurationText,
  liveBreakState,
  staffEndUserLabel,
} from "../lib/liveBreakStaff";
import { allowerLabel, ratePercentText } from "../lib/liveBreaks";
import { useTRPC } from "../lib/trpc";
import { useTenantRole } from "../lib/useTenantRole";

/**
 * One host (boxed's live-break host admin): their standing (the Host Allow List, their rate and
 * the terms their next live break starts at), their commission per pool, their all-time figures
 * and per-box stats, the shoppers they block, and their live breaks. Any end user of the
 * environment reads here, host row or not. Editors allow and remove; admins set the rate and
 * block on their behalf.
 */
export function LiveBreakHostPage() {
  const { tenantId, userId } = useParams({
    from: "/app/tenants/$tenantId/storefront/live-breaks/hosts/$userId",
  });
  const trpc = useTRPC();
  const { can } = useTenantRole();
  const detail = useQuery(trpc.liveBreaks.hosts.get.queryOptions({ endUserId: userId }));
  const data = detail.data;

  if (detail.error) return <ErrorAlert message={detail.error.message} />;
  if (!data) return <Skeleton className="h-96" />;
  const { endUser, host, terms } = data;
  const name = staffEndUserLabel(endUser);

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="flex min-w-0 flex-col gap-2">
          <h1 className="truncate text-xl font-semibold tracking-tight">{name}</h1>
          <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            {host?.allowedAt ? (
              <Badge>On the Host Allow List</Badge>
            ) : (
              <Badge variant="outline">Not on the Host Allow List</Badge>
            )}
            {terms.canHost ? (
              <Badge variant="outline">Can host</Badge>
            ) : (
              <Badge variant="destructive">Can't host while the list is enforced</Badge>
            )}
            {endUser.externalId && <span className="font-mono text-xs">{endUser.externalId}</span>}
            {endUser.email && <span>{endUser.email}</span>}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/tenants/$tenantId/users/$userId"
            params={{ tenantId, userId }}
            search={{ tab: "liveBreaks" }}
            className={buttonVariants({ variant: "outline", size: "sm" })}
          >
            Customer page
          </Link>
          {can("admin") && (
            <LiveBreakHostRateDialog
              host={{
                endUserId: userId,
                username: endUser.username,
                externalId: endUser.externalId,
                rateBps: host?.rateBps ?? null,
              }}
              defaultRateBps={terms.defaultRateBps}
              maxRateBps={terms.maxRateBps}
              triggerVariant="outline"
            />
          )}
          {can("editor") &&
            (host?.allowedAt ? (
              <RemoveLiveBreakHostButton
                host={{
                  endUserId: userId,
                  username: endUser.username,
                  externalId: endUser.externalId ?? userId,
                }}
              />
            ) : (
              <AllowLiveBreakHostButton
                host={{
                  endUserId: userId,
                  username: endUser.username,
                  externalId: endUser.externalId ?? userId,
                }}
              />
            ))}
        </div>
      </header>

      <HostFigures detail={data} />

      <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_24rem]">
        <div className="flex min-w-0 flex-col gap-6">
          <HostLiveBreaksCard tenantId={tenantId} hostId={userId} page={data.liveBreaks} />
          <HostBoxStatsCard rows={data.boxStats} />
        </div>
        <div className="flex min-w-0 flex-col gap-6">
          <CommissionCard detail={data} />
          <TermsCard detail={data} />
          <HostBlocksCard
            tenantId={tenantId}
            hostId={userId}
            hostName={name}
            blocks={data.blocks}
            canEdit={can("admin")}
          />
        </div>
      </div>
    </div>
  );
}

function HostFigures({ detail }: { detail: StaffHostDetailWire }) {
  const currency = useCurrency();
  const { totals, commission, terms } = detail;
  return (
    <Card className="grid grid-cols-2 gap-px bg-border py-0 sm:grid-cols-3 lg:grid-cols-6">
      <div className="bg-card px-5 py-4">
        <Stat label="Live breaks">{formatNumber(totals.liveBreaksHosted)}</Stat>
      </div>
      <div className="bg-card px-5 py-4">
        <Stat label="Next rate">{`${ratePercentText(terms.rateBps)}%`}</Stat>
      </div>
      <div className="bg-card px-5 py-4">
        <Stat label="Viewer spend">{formatAmount(totals.viewerSpendCents, currency)}</Stat>
      </div>
      <div className="bg-card px-5 py-4">
        <Stat label="Opens (viewers · own)">
          {`${formatNumber(totals.viewerOpens)} · ${formatNumber(totals.selfOpens)}`}
        </Stat>
      </div>
      <div className="bg-card px-5 py-4">
        <Stat label="Unclaimed commission">
          {formatAmount(commission.unclaimedCents, currency)}
        </Stat>
      </div>
      <div className="bg-card px-5 py-4">
        <Stat label="Claimed commission">{formatAmount(commission.claimedCents, currency)}</Stat>
      </div>
    </Card>
  );
}

const liveBreakColumns = createDataTableColumns<StaffLiveBreakListItemWire>();

function buildLiveBreakColumns(tenantId: string, currency: Currency, now: Date) {
  const money = (value: unknown) => amountInputValue(Number(value), currency);
  return liveBreakColumns.columns([
    liveBreakColumns.accessor("title", {
      header: "Title",
      size: 220,
      cell: ({ getValue, row }) => (
        <Link
          to="/tenants/$tenantId/storefront/live-breaks/list/$liveBreakId"
          params={{ tenantId, liveBreakId: row.original.id }}
          className="block truncate font-medium hover:underline"
        >
          {getValue()}
        </Link>
      ),
      meta: { filter: "text" },
    }),
    liveBreakColumns.accessor((liveBreak) => LIVE_BREAK_STATE_LABELS[liveBreakState(liveBreak)], {
      id: "state",
      header: "Status",
      cell: ({ getValue, row }) => (
        <Badge variant={LIVE_BREAK_STATE_BADGE[liveBreakState(row.original)]}>{getValue()}</Badge>
      ),
      meta: { filter: "text" },
    }),
    liveBreakColumns.accessor("commissionRateBps", {
      header: "Rate (%)",
      size: 96,
      cell: ({ getValue }) => (
        <span className="font-mono text-xs">{ratePercentText(getValue() ?? 0)}</span>
      ),
      meta: { align: "end", csv: (value) => ratePercentText(Number(value)) },
    }),
    liveBreakColumns.accessor((liveBreak) => liveBreak.totals.viewerSpendCents, {
      id: "viewerSpend",
      header: amountColumnHeader("Viewer spend", currency),
      cell: ({ getValue }) => (
        <span className="font-mono text-xs">{formatAmount(getValue(), currency)}</span>
      ),
      meta: { align: "end", filter: "number", filterCents: true, csv: money },
    }),
    liveBreakColumns.accessor(
      (liveBreak) => liveBreak.totals.viewerCommissionCents + liveBreak.totals.selfCommissionCents,
      {
        id: "commission",
        header: amountColumnHeader("Commission", currency),
        cell: ({ getValue }) => (
          <span className="font-mono text-xs">{formatAmount(getValue(), currency)}</span>
        ),
        meta: { align: "end", filter: "number", filterCents: true, csv: money },
      },
    ),
    liveBreakColumns.accessor("createdAt", {
      header: "Started",
      cell: ({ getValue }) => <ShortDate value={getValue()} />,
    }),
    liveBreakColumns.accessor((liveBreak) => liveBreakDurationText(liveBreak, now), {
      id: "length",
      header: "Length",
      size: 104,
      enableSorting: false,
    }),
  ]);
}

/** Their live breaks, newest first: the first page here, the rest on the filtered list. */
function HostLiveBreaksCard({
  tenantId,
  hostId,
  page,
}: {
  tenantId: string;
  hostId: string;
  page: StaffHostDetailWire["liveBreaks"];
}) {
  const currency = useCurrency();
  const [now] = useState(() => new Date());
  const columns = useMemo(
    () => buildLiveBreakColumns(tenantId, currency, now),
    [tenantId, currency, now],
  );
  return (
    <Card>
      <CardHeader>
        <CardTitle>Live breaks</CardTitle>
        <CardDescription>Newest first.</CardDescription>
        <CardAction>
          <Link
            to="/tenants/$tenantId/storefront/live-breaks/list"
            params={{ tenantId }}
            search={{ host: hostId }}
            className={buttonVariants({ variant: "outline", size: "sm" })}
          >
            {page.nextCursor ? "See all" : "Open in the list"}
          </Link>
        </CardAction>
      </CardHeader>
      <CardContent>
        <DataTable
          columns={columns}
          data={page.liveBreaks}
          getRowId={(liveBreak) => liveBreak.id}
          search={false}
          csvName={`host-${hostId}-live-breaks`}
          emptyMessage="They haven't hosted a live break."
        />
      </CardContent>
    </Card>
  );
}

const boxStatColumns = createDataTableColumns<StaffLiveBreakBoxStatsWire>();

function buildBoxStatColumns(currency: Currency) {
  const money = (value: unknown) => amountInputValue(Number(value), currency);
  return boxStatColumns.columns([
    boxStatColumns.accessor("boxName", {
      header: "Box",
      size: 220,
      cell: ({ getValue, row }) => (
        <span className="flex min-w-0 items-center gap-2">
          <BoxImage
            src={row.original.boxImageUrl}
            className="size-8 shrink-0 rounded border object-contain"
          />
          <span className="truncate">{getValue()}</span>
        </span>
      ),
      meta: { filter: "text" },
    }),
    boxStatColumns.accessor("viewerOpens", {
      header: "Viewer opens",
      meta: { align: "end", filter: "number" },
    }),
    boxStatColumns.accessor("viewerSpendCents", {
      header: amountColumnHeader("Viewer spend", currency),
      cell: ({ getValue }) => (
        <span className="font-mono text-xs">{formatAmount(getValue(), currency)}</span>
      ),
      meta: { align: "end", filter: "number", filterCents: true, csv: money },
    }),
    boxStatColumns.accessor("selfOpens", {
      header: "Own opens",
      meta: { align: "end", filter: "number" },
    }),
    boxStatColumns.accessor("selfSpendCents", {
      header: amountColumnHeader("Own spend", currency),
      cell: ({ getValue }) => (
        <span className="font-mono text-xs">{formatAmount(getValue(), currency)}</span>
      ),
      meta: { align: "end", filter: "number", filterCents: true, csv: money },
    }),
  ]);
}

/** Lifetime per box across their live breaks, most viewer spend first. */
function HostBoxStatsCard({ rows }: { rows: StaffLiveBreakBoxStatsWire[] }) {
  const currency = useCurrency();
  const columns = useMemo(() => buildBoxStatColumns(currency), [currency]);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Boxes</CardTitle>
        <CardDescription>
          Every box opened in their live breaks, split between viewers' purchases and their own.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <DataTable
          columns={columns}
          data={rows}
          getRowId={(row) => row.boxId}
          search="Search boxes"
          csvName="host-box-stats"
          emptyMessage="Nothing has opened in their live breaks yet."
        />
      </CardContent>
    </Card>
  );
}

/** Their commission per pool: what a claim would pay now, and what claims have paid. */
function CommissionCard({ detail }: { detail: StaffHostDetailWire }) {
  const currency = useCurrency();
  const { commission } = detail;
  return (
    <FactGroup title="Commission">
      <FactList>
        <Fact label="Viewer, unclaimed" mono>
          {formatAmount(commission.viewer.unclaimedCents, currency)}
        </Fact>
        <Fact label="Viewer, claimed" mono>
          {formatAmount(commission.viewer.claimedCents, currency)}
        </Fact>
        <Fact label="Own, unclaimed" mono>
          {formatAmount(commission.self.unclaimedCents, currency)}
        </Fact>
        <Fact label="Own, claimed" mono>
          {formatAmount(commission.self.claimedCents, currency)}
        </Fact>
      </FactList>
      <p className="mt-3 text-xs text-muted-foreground">
        Own commission comes back to the host on their own purchases. They claim both pools into
        their balance from the storefront's host dashboard.
      </p>
    </FactGroup>
  );
}

/** Their standing: the rate their next live break starts at and the environment's terms. */
function TermsCard({ detail }: { detail: StaffHostDetailWire }) {
  const { host, terms } = detail;
  return (
    <FactGroup title="Terms">
      <FactList>
        <Fact label="Own rate">
          {terms.ownRateBps === null
            ? "Follows the default"
            : `${ratePercentText(terms.ownRateBps)}%${terms.ownRateBps > terms.maxRateBps ? " (above the maximum, read capped)" : ""}`}
        </Fact>
        <Fact label="Default rate">{`${ratePercentText(terms.defaultRateBps)}%`}</Fact>
        <Fact label="Maximum rate">{`${ratePercentText(terms.maxRateBps)}%`}</Fact>
        <Fact label="Allowed">
          {host?.allowedAt
            ? `${formatDate(host.allowedAt)}${host.allowedBy ? ` · ${allowerLabel(host.allowedBy)}` : ""}`
            : "—"}
        </Fact>
        <Fact label="Host since">{host ? formatDate(host.createdAt) : "No host row yet"}</Fact>
      </FactList>
    </FactGroup>
  );
}

/** The shoppers they block from every live break of theirs; admins block on their behalf. */
function HostBlocksCard({
  tenantId,
  hostId,
  hostName,
  blocks,
  canEdit,
}: {
  tenantId: string;
  hostId: string;
  hostName: string;
  blocks: StaffBlockWire[];
  canEdit: boolean;
}) {
  return (
    <Card size="sm">
      <CardHeader className="min-h-8 items-center">
        <CardTitle>Blocks</CardTitle>
        {canEdit && (
          <CardAction className="self-center">
            <BlockShopperDialog
              hostEndUserId={hostId}
              setName={hostName}
              blockedIds={new Set(blocks.map((block) => block.endUser.endUserId))}
            />
          </CardAction>
        )}
      </CardHeader>
      <CardContent>
        {blocks.length === 0 ? (
          <p className="text-sm text-muted-foreground">They block no one.</p>
        ) : (
          <ul className="divide-y">
            {blocks.map((block) => (
              <li key={block.id} className="flex items-center justify-between gap-2 py-2">
                <div className="grid min-w-0 gap-0.5">
                  <Link
                    to="/tenants/$tenantId/users/$userId"
                    params={{ tenantId, userId: block.endUser.endUserId }}
                    className="truncate text-sm font-medium hover:underline"
                  >
                    {staffEndUserLabel(block.endUser)}
                  </Link>
                  <span className="text-xs text-muted-foreground">
                    {block.blockedBy === "host" ? "By the host" : "By staff"} ·{" "}
                    <ShortDate value={block.createdAt} />
                  </span>
                </div>
                {canEdit && <UnblockButton block={block} />}
              </li>
            ))}
          </ul>
        )}
        <Link
          to="/tenants/$tenantId/storefront/live-breaks/blocks"
          params={{ tenantId }}
          search={{ scope: "host", host: hostId }}
          className="mt-2 inline-block text-xs text-primary hover:underline"
        >
          Open in Blocks
        </Link>
      </CardContent>
    </Card>
  );
}
